/// <reference path="../registerCreditPaymentSectionBase.ts" />
module Meevo.Plugins.Register {
    "use strict";

    export class CreditPaymentsSection extends Meevo.Plugins.Register.RegisterCreditPaymentSectionBase {
        useCreditCardOnFile: boolean = false;
        creditCardSelected: Model.PaymentAccount;
        payingClientCreditCardList: Model.PaymentAccount[] =[];
        nonPayingClientCreditCardList: Model.PaymentAccount[]=[];
        showPaymentOptions: boolean = false;
        shouldShowCcOptions: boolean = false;
        shouldProcessPayment: boolean = false;
        displayPaymentAmount: boolean = false;
        proceedWithPayment: boolean = false;
        cardPaymentList: any[];
        unallocatedAmountToRefund: number = 0;
        cardCreditRequestList: any[];
        cardReceiptList: any[];
        forceManualEntry: boolean = false;
        billingPostalCode: string;
        headerTextKey: string;
        additionalClient: any;
        additionalClients: any[];
        surcharges: Model.MerchantAccountSurcharge[];
        hasHsaFsaMerchantAccount: boolean;
        requireSecurityCode: boolean;
        encryptedSecurityCodeProp = "encryptedSecurityCode";
        externalTipAmount: number;
        fullBillingAddressRequired: boolean = false;

        private readonly hostedPageId: Meevo.Model.HostedPageIdType = 'HostedPageCreditPayment';
        private readonly securityCodeHostedPageId: Meevo.Model.HostedPageIdType = 'HostedPageCardOnFileSecurityCode';

        constructor(
            registerContext: Meevo.Plugins.Register.RegisterContext,
            sectionType: Meevo.Plugins.Register.RegisterSectionTypeEnum
        ) {
            super(sectionType, registerContext, "creditPayments", Register.RegisterSectionCatTypeEnum.Payment);

            switch (this.sectionType) {
                case RegisterSectionTypeEnum.CardOnFile:
                    this.headerTextKey = "LABEL_CARD_ON_FILE";
                    break;
                default:
                    this.headerTextKey = "LABEL_CREDIT_PAYMENT_TYPES";
                    break;
            }

            this.creditPaymentTypes = _(registerContext.paymentTypes).filter((paymentType: Model.PaymentTypeListItem) =>
                (
                    paymentType.paymentCategory === Model.PaymentCategoryEnum.CreditDebit ||
                    paymentType.paymentCategory === Model.PaymentCategoryEnum.Debit
                ) &&
                paymentType.paymentSubCategory !== Model.PaymentSubCategoryEnum.Unknown
            );

            this.setHasHsaFsaMerchantAccounts();
        }

        isHostedPageActive() {
            return super.isHostedPageActive(this.hostedPageId);
        }

        isSecurityCodeActive() {
            return super.isHostedPageActive(this.securityCodeHostedPageId);
        }

        isCollectFullBillingAddressVisible(): boolean {
            return this.registerContext.isMeevoPayProvider() &&
                this.isHostedPage() && 
                this.isHostedPageActive();
        }

        collectFullBillingAddressChanged() {
            this.hostedPageService.updateBillingAddressMode(this.hostedPageId, this.fullBillingAddressRequired);
        }

        private setHasHsaFsaMerchantAccounts() {
            this.registerContext.getHasHsaFsaMerchantAccounts().then((result) => {
                this.hasHsaFsaMerchantAccount = result;
            });
        }

        creditPaymentTypes: any[];
        selectedPaymentType: Model.PaymentTypeListItem;

        public addPaymentDisabled() {
            if (!this.validateAmount()) {
                return true;
            }

            if (this.registerContext.sale.changeDue === 0) {
                return true;
            }

            if (this.isCardOnFile() && !this.useCreditCardOnFile) {
                return true;
            }

            return super.addPaymentDisabled();
        }

        public isProcessCreditDisabled() {
            if (!this.validateAmount()) {
                return true;
            }

            if (this.registerContext.sale.changeDue === 0) {
                return true;
            }

            if (this.isCardOnFile() && !this.useCreditCardOnFile) {
                return true;
            }

            return this.disablePayment();
        }

        disablePayment() {
            if (this.shouldProcessPayment) {
                if (this.creditCardProcessingEnabled) {
                    return false;
                }

                if (this.showPaymentOptions && this.payment.amount > 0) {
                    return false;
                }
            }

            return true;
        }

        selectPaymentType(paymentType: Model.PaymentTypeListItem) {
            this.selectedPaymentType = paymentType;
            this.payment.paymentTypeId = paymentType ? paymentType.id : undefined;
            super.addPayment();
            super.returnToOptions();
        }

        processCreditClicked() {
            this.registerContext.registerController.usageTrackingLog(Meevo.Model.VerbEnum.ProcessCreditClicked);
            this.addPayment();
        }

        addPayment() {
            if (!this.creditCardProcessingEnabled) {
                this.shouldShowCcOptions = true;
                return;
            }

            if (this.registerContext.isCreditMode) {
                if (this.registerContext.getMerchantAccountHasLinkedRefundSupport()) {
                    this.processCredit();
                }
                else {
                    this.processCreditUnlinked();
                }
            }
            else {
                if (this.isCardOnFile()) {
                    this.payment = this.getNewPayment();
                }

                this.processPayment();
            }
        }

        processPartial() {
            this.payment = this.getNewPayment();

            if (this.externalTipAmount > 0) {
                this.payment.amount = this.externalTipAmount;
            }

            this.showPaymentOptions = true;
            this.shouldProcessPayment = true;
            this.showAddPaymentButton = true;
            this.hostedPageService.reset(this.hostedPageId);
        }

        processFull() {
            this.payment = this.getNewPayment();

            this.addBalanceAsPayment();

            if (this.creditCardProcessingEnabled) {
                this.showPaymentOptions = false;
                this.shouldProcessPayment = true;
                this.showAddPaymentButton = false;
                this.addPayment();
            }
            else {
                this.showPaymentOptions = true;
                this.shouldShowCcOptions = true;
            }
        }

        // this is called from register.context selectSection method when selected "Credit Card (Single Card)"" payment tile
        processSingleCard() {
            if (this.registerContext.isCreditMode) {
                return;
            }

            this.processFull();
        }

        hasItems() {
            return this.payingClientCreditCardList.length > 0 || this.nonPayingClientCreditCardList.length > 0;
        }

        fullReset() {
            this.cardPaymentList = undefined;
            this.reset();
        }

        reset() {
            this.payment = this.getNewPayment();

            if (this.creditCardProcessingEnabled) {
                this.addBalanceAsPayment();
            }

            this.showPaymentOptions = false;
            this.shouldShowCcOptions = false;
            this.shouldProcessPayment = false;
            this.displayPaymentAmount = false;
            this.proceedWithPayment = false;
            this.forceManualEntry = false;
            this.fullBillingAddressRequired = false;
            this.billingPostalCode = undefined;
            this.hostedPageService.reset(this.hostedPageId);
            this.hostedPageService.reset(this.securityCodeHostedPageId);
            this.requireSecurityCode = false;
            this.payingClientCreditCardList = [];
            this.nonPayingClientCreditCardList = [];
            this.creditCardSelected = null;
            this.externalTipAmount = this.registerContext.hasMeevoPaySplitFunding()
                ? this.registerContext.getExternalTipAmountDue()
                : 0;

            this.checkCreditCardOnFile();
            this.loadSurcharges();

            this.unallocatedAmountToRefund = 0;
            if (this.registerContext.isCreditMode && this.creditCardProcessingEnabled && !this.cardPaymentList) {
                this.getPaymentRequestBasedOnRefundItems();
            }
        }

        validate(otherCard: boolean = false) {
            if (!this.validateAmount()) {
                return false;
            }

            if (!this.registerContext.selectedDrawer.merchantAccountId) {
                this.modalService.alert("MESSAGE_MISSING_MERCHANT_ACCOUNT");
                return false;
            }

            if (otherCard && this.registerContext.getMerchantTerminalId() == null) {
                this.modalService.alert("MESSAGE_MISSING_CREDIT_CARD_TERMINAL");
                return false;
            }

            if (this.registerContext.hasPendingPayment()) {
                this.modalService.alert("MESSAGE_PENDING_CREDIT_CARD_PAYMENT");
                return false;
            }

            return true;
        }

        private processTipEntryMethod(): ng.IPromise<Meevo.Model.TipEntryMethodEnum> {
            const defer = this.$q.defer<Meevo.Model.TipEntryMethodEnum>();

            let tipSetting = this.registerContext.selectedDrawer.tipEntryMethodEnum;

            if (tipSetting === Meevo.Model.TipEntryMethodEnum.Prompt || tipSetting === Meevo.Model.TipEntryMethodEnum.Terminal) {
                if (!this.registerContext.isDeviceTipAvailable(true)) {
                    console.log('processTipEntryMethod', 'Forcing TipEntry OnScreen option due to credit card tip not being available.');
                    tipSetting = Meevo.Model.TipEntryMethodEnum.OnScreen;
                }
            }

            if (tipSetting !== Meevo.Model.TipEntryMethodEnum.Prompt) {
                defer.resolve(tipSetting);
                return defer.promise;
            }

            this.registerContext.showModal(RegisterModalTypeEnum.TipEntry).then((response) => {
                switch (response) {
                    case Meevo.Model.TipEntryMethodEnum.Terminal:
                    case Meevo.Model.TipEntryMethodEnum.OnScreen:
                        defer.resolve(response);
                        break;
                    default:
                        defer.reject();
                        break;
                }
            });

            return defer.promise;
        }

        private processSignatureEntryMethod(forcePrompt: boolean = false): ng.IPromise<Meevo.Model.SignatureEntryMethodEnum> {
            const defer = this.$q.defer<Meevo.Model.SignatureEntryMethodEnum>();

            let signatureSetting = forcePrompt
                ? Meevo.Model.SignatureEntryMethodEnum.Prompt
                : this.registerContext.selectedDrawer.signatureEntryMethodEnum;

            if (signatureSetting !== Meevo.Model.SignatureEntryMethodEnum.Prompt) {
                defer.resolve(signatureSetting);
                return defer.promise;
            }

            this.registerContext.showModal(RegisterModalTypeEnum.SignatureEntry).then((response) => {
                switch (response) {
                    case Meevo.Model.SignatureEntryMethodEnum.OnReceipt:
                    case Meevo.Model.SignatureEntryMethodEnum.OnScreen:
                    case Meevo.Model.SignatureEntryMethodEnum.OnTerminal:
                        defer.resolve(response);
                        break;
                    default:
                        defer.reject();
                        break;
                }
            });

            return defer.promise;
        }

        private signatureModal() {
            let options: Meevo.Components.Modal.IModalOptions = {
                templateUrl: "app/plugins/register/modals/signatureModal.template.html",
                controller: "meevo.plugins.register.SignatureModalController",
                controllerAs: "vm",
                bindToController: true,
                locals: {
                    paymentAmount: this.payment.amount
                }
            }
            return this.modalService.showCustom(options);
        }

        private signatureTerminal(): ng.IPromise<any> {
            const selectedTerminal = this.registerContext.selectedTerminal;
            if (!selectedTerminal) {
                console.error("No terminal selected for signature capture.");
                return;
            }

            let defer = this.$q.defer();

            this.busyService.start("MESSAGE_TERMINAL_SIGNATURE", true);

            const request = {
                externalTerminalId: selectedTerminal.externalTerminalId,
                merchantTerminalId: selectedTerminal.id,
                merchantAccountId: selectedTerminal.merchantAccountId,
            };

            const url = `api/Drawer/${this.registerContext.selectedDrawer.id}/SignaturePrompt`;

            this.dataService.customMethodData(url, "POST", request)
                .then((response) => {
                    if (response.status === 409) { // conflict
                        this.modalService.alert(response.data.responseStatus.message);
                        defer.reject();
                        return;
                    }

                    let signatureResponse = response.data;

                    defer.resolve(signatureResponse);
                })
                .catch(() => {
                    defer.reject();
                })
                .finally(() => {
                    this.busyService.stop();
                });

            return defer.promise;
        }

        private activateSecurityCodeHostedPage() {
            const request: Meevo.Components.HostedPage.IHostedPageRequest = {
                merchantAccountId: this.registerContext.getMerchantAccountId(),
                merchantProviderId: this.registerContext.getMerchantProviderId(),
                externalPaymentAccountId: this.creditCardSelected.externalPaymentAccountId,
                isSecurityCode: true,
                cardType: this.creditCardSelected.cardType,
            };

            return this.hostedPageService.process(request, this.securityCodeHostedPageId)
                .then((securityCodeResponse) => {
                    return securityCodeResponse;
                });
        };

        processPayment() {
            const encryptedSecurityCode = (this.creditCardSelected || {})[this.encryptedSecurityCodeProp];
            if (this.isCardOnFile()) {
                this.addBalanceAsPayment();

                if (this.requireSecurityCode)
                {
                    this.creditCardSelected[this.encryptedSecurityCodeProp] = undefined;
                    if (angular.isUndefined(encryptedSecurityCode)) {
                        this.hostedPageService.collectSecurityCode(this.securityCodeHostedPageId);
                        return;
                    }
                }
            }

            if (!this.validate()) {
                return null;
            }

            const continueExecution = (response: any = null) => {
                // if we need to block any payment that will require processing, do it here!

                this.busyService.start("LABEL_CREDIT_DEBIT_PENDING_PROCESSING", true);

                // create a payment record and have credit card processor process the payment
                this.payment.requestedAmount = parseFloat(this.payment.amount.toString());
                if (response) {
                    this.payment.surcharge = response.surcharge;
                }

                const request = {
                    paymentRequest: this.payment,
                    merchantTerminalId: null,
                    externalTerminalId: null,
                    merchantTerminalSerialNumber: null,
                    merchantAccountId: this.registerContext.selectedDrawer.merchantAccountId,
                    sale: this.registerContext.sale,
                    paymentAccountId: null,
                    forceManualEntry: this.forceManualEntry,
                    billingPostalCode: this.billingPostalCode,
                    transactionSetupId: null,
                    transactionId: null,
                    paymentRequestId: null,
                    onHoldId: null,
                    isECommerce: false,
                    encryptedSecurityCode: null,
                    hasMeevoPaySplitFunding: this.registerContext.hasMeevoPaySplitFunding()
                };

                if (!this.isHostedPage() && !this.isCardOnFile()) {
                    if (this.registerContext.selectedTerminal) {
                        request.merchantTerminalId = this.registerContext.selectedTerminal.id;
                        request.externalTerminalId = this.registerContext.selectedTerminal.externalTerminalId;
                        request.merchantTerminalSerialNumber = this.registerContext.selectedTerminal.serialNumber;
                    }
                }

                if (this.isCardOnFile() && this.useCreditCardOnFile && this.creditCardSelected) {
                    request.paymentAccountId = this.creditCardSelected.id;
                    request.encryptedSecurityCode = encryptedSecurityCode;
                }

                if (this.isHostedPage()) {
                    request.transactionSetupId = response.transactionSetupId;
                    request.transactionId = response.transactionId;
                    request.paymentRequestId = response.paymentRequestId;
                    request.onHoldId = response.onHoldId;
                    // payment request & hold may be modified if payment was retried
                    request.paymentRequest.paymentRequestId = response.paymentRequestId;
                    request.paymentRequest.onHoldId = response.onHoldId;
                }

                const promises = [];

                if (!request.paymentRequestId) {
                    const automaticChangeDue = request.paymentAccountId ? false : true;
                    // add the payment to the smart receipt
                    promises.push(super.addPayment(false, automaticChangeDue));
                }

                return this.$q.all(promises).then(() => {
                    return this.dataService
                        .customMethodData(`api/Drawer/${this.registerContext.selectedDrawer.id}/PaymentRequest`, "POST", request)
                        .then(this.processPaymentSuccessCallback, this.processPaymentErrorCallback)
                        .finally(() => {
                            this.showPaymentOptions = false;
                            this.showAddPaymentButton = false;
                            this.externalTipAmount = 0;
                            this.payment = this.getNewPayment();
                            this.addBalanceAsPayment();
                            this.busyService.stop();
                        });
                });
            };

            let signatureResponse = null;
            let tipResponse = null;

            const signatureExecution = (forcePrompt: boolean = false) => {
                this.addBalanceAsPayment();

                this.processSignatureEntryMethod(forcePrompt).then((response) => {
                    signatureResponse = response;
                    switch (signatureResponse) {
                        case Meevo.Model.SignatureEntryMethodEnum.OnReceipt:
                            continueExecution();
                            break;

                        case Meevo.Model.SignatureEntryMethodEnum.OnScreen:
                            this.signatureModal().then((response) => {
                                this.payment.signatureData = response;
                                this.payment.signatureDataFormat = Meevo.Model.CreditCardSignatureFormatEnum.Base64PNG;
                                continueExecution();
                            });
                            break;

                        case Meevo.Model.SignatureEntryMethodEnum.OnTerminal:
                            this.signatureTerminal().then((response) => {
                                if (response.isCancelled) {
                                    return this.modalService.alert("MESSAGE_TERMINAL_SIGNATURE_CANCELLED").then(() => {
                                        signatureExecution(true);
                                    });
                                }
                                else if (response.isUnsupported) {
                                    return this.modalService.alert("MESSAGE_TERMINAL_SIGNATURE_UNSUPPORTED").then(() => {
                                        signatureExecution(true);
                                    });
                                }
                                this.payment.signatureData = response.signatureData;
                                this.payment.signatureDataFormat = response.signatureDataFormat;
                                continueExecution();
                            });
                            break;
                    }
                });
            };

            const tipExecution = () => {
                this.processTipEntryMethod().then((response) => {
                    tipResponse = response;

                    const modalType = signatureResponse === Meevo.Model.SignatureEntryMethodEnum.OnReceipt
                        ? RegisterModalTypeEnum.Tip
                        : RegisterModalTypeEnum.TipCardOnFile;

                    switch (tipResponse) {
                        case Meevo.Model.TipEntryMethodEnum.Terminal:
                            this.registerContext.deviceTip(modalType).then(() => {
                                signatureExecution();
                            });
                            break;

                        case Meevo.Model.TipEntryMethodEnum.OnScreen:
                            // when there is a signature prompt or on-screen, then tip modal changes slightly
                            this.registerContext.showTips(modalType).then(() => {
                                signatureExecution();
                            });
                            break;
                    }
                });
            };

            const hostedPage = () => {
                this.showPaymentOptions = false;

                const request: Meevo.Components.HostedPage.IHostedPageRequest = {
                    drawerId: this.registerContext.selectedDrawer.id,
                    merchantAccountId: this.registerContext.getMerchantAccountId(),
                    merchantProviderId: this.registerContext.getMerchantProviderId(),
                    transactionAmount: this.payment.amount,
                    taxAmount: this.payment.taxAmount,
                    sale: this.registerContext.sale,
                    paymentLineTempId: this.payment.tempId,
                    tokenCreatedCallback: (setupId: string, paymentRequestId: any, onHoldId: any) => {
                        this.payment.transactionSetupId = setupId;
                        this.payment.paymentRequestId = paymentRequestId;
                        if (onHoldId) {
                            this.payment.onHoldId = onHoldId;
                            this.setSaleOnHoldId(onHoldId);
                        }
                    },
                    allowPartialApprovals: true,
                    billingPostalCodeRequired: true,
                    fullBillingAddressRequired: this.fullBillingAddressRequired,
                    fullBillingAddressLookup: this.registerContext.isMeevoPayProvider(),
                    hasMeevoPaySplitFunding: this.registerContext.hasMeevoPaySplitFunding(),
                };

                const deferred = this.$q.defer();

                // add the payment to the smart receipt
                super.addPayment(false, false);

                this.hostedPageService.process(request, this.hostedPageId)
                    .then((response: any) => {
                        this.payment.transactionSetupId = null;

                        continueExecution(response);

                        deferred.resolve();
                    })
                    .catch((response: any) => {
                        this.processPaymentErrorCallback(response);

                        deferred.reject();
                    });

                return deferred.promise;
            };

            if (this.isHostedPage()) {
                hostedPage();
            } else if (this.isCardOnFile()) {
                if (this.registerContext.isTipEligible() && !this.registerContext.sale.externalTipTransactionId) {
                    tipExecution();
                } else {
                    signatureExecution();
                }
            } else {
                continueExecution();
            }
        }
/*
		// test method for egift - add a button to html to call this - leaving it here in case we need to test in future

        processEgiftSale() {
            const egiftInitializeSetupUrl = `api/sale/egift/initialize`;

            const egiftInitializeRequest = {
                clientId: 'b0dd6a98-af97-4b7e-9d1c-a5f50110904d',
                items: [{
                    giftCardId: 'f4ab2871-69e6-4d45-bdfb-a93101171d9d',
                    discountId: '3d2ed192-cac8-48f2-82ba-a9310117402f',
                }]
            };

            this.dataService.customMethodData(egiftInitializeSetupUrl, "POST", egiftInitializeRequest).then((response: any) => {
                const hostedPageRequest = {
                    merchantAccountId: this.registerContext.selectedDrawer.merchantAccountId,
                    transactionAmount: response.data.transactionAmount,
                    referenceNumber: response.data.referenceNumber,
                    returnURL: 'http://localhost:8700/api/sale/egift/finalize',
                    returnURLTitle: 'Meevo'
                };

                const hostedPageSetupUrl = `api/Drawer/${this.registerContext.selectedDrawer.id}/HostedPageSetup`;

                return this.dataService.customMethodData(hostedPageSetupUrl, "POST", hostedPageRequest).then((hostedPageSetupResponse: any) => {
                    if (hostedPageSetupResponse.status == 409) { // conflict
                        this.processPaymentErrorCallback(hostedPageSetupResponse);
                        return;
                    }

                    $('#HostedPageCreditPayment iframe').attr('src', hostedPageSetupResponse.data.hostedPageUrl);
                })
                .catch((response: any) => {
                    if (response.status == 409) { // conflict
                        this.processPaymentErrorCallback(response);
                        return;
                    }
                });
            });
        }
*/
        processApprovedPayment(paymentRequest: Model.PaymentRequest, paymentLine: Model.SaleCreditCardPayment): ng.IPromise<any> {
            let defer = this.$q.defer();

            const finishPayment = () => {
                if (paymentLine && paymentLine.requestedAmount > paymentLine.approvedAmount) {
                    // ask if customer wants to keep partial payment
                    let meevoCurrency = this.$filter('meevoCurrency') as any;
                    let msg = this.$translate.instant("MESSAGE_PARTIAL_PAYMENT", { approvedAmount: meevoCurrency(paymentLine.approvedAmount) });
                    this.busyService.stop();
                    return this.modalService.confirmHandler(msg).then((result) => {
                        if (!result) {
                            // do not keep partial payment, remove payment
                            // - CareCredit: perform refresh (check if finalized or not)
                            // - other cards: cancel
                            this.registerContext.refreshPaymentRequest(paymentLine);
                        }
                    });
                }

                if (this.registerContext.isCreditMode) {
                    this.registerContext.recalculateChangeDue();
                }

                this.registerContext.printCreditCardReceipts([ paymentRequest ]);

                if (this.registerContext.getChangeDue() > 0 || this.registerContext.checkIfPaymentIsEnough()) {
                    this.registerContext.finishSection();
                }

                defer.resolve();
            };

            const continueExecution = () => {
                this.registerContext.updatePaymentLineWithPaymentRequest(paymentRequest, paymentLine);

                // if surcharge exists, additional salelines need to be generated on server-side
                if (!paymentRequest.surcharge) {
                    this.registerContext.recalculateChangeDue();
                    finishPayment();
                } else {
                    this.registerContext.recalculateSale().then(() => {
                        this.registerContext.sale.saleStage = Model.SaleStageEnum.Payment;
                        finishPayment();
                    });
                }
            };

            if (this.registerContext.hasPaymentAlertMessage(paymentRequest.creditCardProcessorStatus)) {
                this.busyService.stop();
                this.modalService.alert(paymentRequest.creditCardProcessorStatus.paymentAlertMessage).then(() => {
                    this.registerContext.registerController.trackUsage(Model.NounEnum.CreditCard, Model.VerbEnum.WarningMessageOkButtonClicked);
                    continueExecution();
                });
            } else {
                continueExecution();
            }

            return defer.promise;
        }

        setSaleOnHoldId(onHoldId, holdType?) {
            this.registerContext.setSaleOnHoldId(onHoldId, holdType);
            this.registerContext.updateSelfPaymentWithOnHoldId(onHoldId, false);
        }

        processPaymentSuccessCallback = (response) => {
            if (response.status == 409) { // conflict
                this.processPaymentErrorCallback(response);
                return;
            }

            // need to get the paymentTypeId/paymentType
            let paymentLine: Model.SaleCreditCardPayment = _.findWhere(this.registerContext.sale.paymentLines, { isPending: true });

            let processedPayment: Model.PaymentRequest = response.data;

            if (processedPayment.paymentRequestStatus === Model.PaymentRequestStatusEnum.SignatureVerificationPending) {
                this.registerContext.printCreditCardReceiptsWithData([processedPayment]);

                this.busyService.stop();
                return this.modalService.confirmHandler("MESSAGE_ACCEPT_SIGNATURE", Components.Modal.ModalTranslationModes.Translate).then((response) => {
                    this.busyService.start();
                    let signatureRequest = {
                        merchantTerminalId: processedPayment.merchantTerminalId,
                        externalTerminalId: processedPayment.externalTerminalId,
                        sale: this.registerContext.sale,
                        paymentRequestTempId: paymentLine.tempId,
                        onHoldId: processedPayment.onHoldId,
                        accepted: response
                    };
                    return this.dataService.customMethodData(`api/Drawer/${this.registerContext.selectedDrawer.id}/PaymentRequest/${processedPayment.id}/Signature`, "POST", signatureRequest)
                        .then((signatureResponse) => {
                            let processedPayment: Model.PaymentRequest = signatureResponse.data;
                            if (processedPayment.processorResponseCode == Model.ProcessorResponseCodeEnum.SignatureRejected) {
                                this.registerContext.removeTicketPaymentLine(paymentLine);
                            }
                            else if (processedPayment.processorResponseCode == Model.ProcessorResponseCodeEnum.ApprovedWithSignatureVerificationTimeout) {
                                this.busyService.stop();
                                return this.modalService.alert("MESSAGE_CC_STATUS_APPROVED_WITH_SIGNATURE_TIMEOUT")
                                    .then(() => {
                                        this.busyService.start();
                                        return this.processPaymentSuccessCallback(signatureResponse);
                                    });
                            }
                            return this.processPaymentSuccessCallback(signatureResponse);
                        }, this.processPaymentErrorCallback);
                });
            }

            if (processedPayment.onHoldId) {
                this.setSaleOnHoldId(processedPayment.onHoldId, processedPayment.holdType);
            }

            if (processedPayment.paymentRequestStatus === Model.PaymentRequestStatusEnum.Approved) {
                return this.processApprovedPayment(processedPayment, paymentLine);
            }

            if (processedPayment.creditCardProcessorStatus &&
                processedPayment.creditCardProcessorStatus.code == Model.ProcessingStatusEnum.Timeout &&
                this.registerContext.selectedMerchantAccount.hasTimeoutSupport
            ) {
                this.initiateTimeoutWorkflow(paymentLine);
                return;
            }

            const hostedPageSwitchover = () => {
                const paymentAmount = paymentLine.amount;
                this.registerContext.removeTicketPaymentLine(paymentLine);
                this.addBalanceAsPayment();
                this.payment.amount = paymentAmount;
                this.processPayment();
            }

            const transactionQuery = (isHostedPageSwitch: boolean) => {
                this.modalService.alert("MESSAGE_CREDIT_CARD_PROCESSOR_QUERY").then(() => {
                    this.busyService.start();

                    const statusRetryRequest = {
                        sale: this.registerContext.sale,
                        paymentRequest: this.payment,
                        onHoldId: processedPayment.onHoldId
                    };

                    this.dataService.customMethodData(`api/paymentRequest/${processedPayment.id}/statusRetry`, "POST", statusRetryRequest).then(statusRetryResponse => {
                        let queryPayment: Model.PaymentRequest = statusRetryResponse.data;
                        queryPayment.performTransactionQuery = false;

                        if (queryPayment.processorResponseCode === Model.ProcessorResponseCodeEnum.NotDefined) {
                            this.modalService.alert("MESSAGE_CREDIT_CARD_PROCESSOR_QUERY_UNKNOWN").then(() => {
                                if (isHostedPageSwitch) {
                                    hostedPageSwitchover();
                                } else {
                                    this.processPaymentSuccessCallback(statusRetryResponse);
                                }
                            });
                        }
                        else if (queryPayment.processorResponseCode !== Model.ProcessorResponseCodeEnum.Success) {
                            this.modalService.alert("MESSAGE_CREDIT_CARD_PROCESSOR_QUERY_UNSUCCESSFUL").then(() => {
                                if (isHostedPageSwitch) {
                                    hostedPageSwitchover();
                                } else {
                                    this.processPaymentErrorCallback(statusRetryResponse);
                                }
                            });
                        } else {
                            this.processPaymentSuccessCallback(statusRetryResponse);
                        }
                    }).finally(() => {
                        this.busyService.stop();
                    });
                }, this.processPaymentErrorCallback);
            };

            if (!this.registerContext.selectedMerchantAccount.hasTimeoutSupport && processedPayment.performTransactionQuery) {
                if (this.registerContext.selectedTerminal.merchantTerminalType === Model.MerchantTerminalTypeEnum.Physical &&
                    this.registerContext.selectedMerchantAccount.isHostedPageAutomaticallySwitch &&
                    processedPayment.creditCardProcessorStatus &&
                    (processedPayment.creditCardProcessorStatus.code === Model.ProcessingStatusEnum.ProcessorCommunicationError ||
                     processedPayment.creditCardProcessorStatus.code === Model.ProcessingStatusEnum.RequestTimeout)
                ) {
                    const url = `api/merchantAccount/${this.registerContext.selectedDrawer.merchantAccountId}/devices/list`;
                    const request = {
                        criteria: {
                            isHostedMode: true,
                        }
                    };
                    this.dataService.customMethodData(url, "POST", request)
                        .then((response: any) => {
                            const hostedPageTerminal = response.data.find(terminal => terminal.merchantTerminalType === Model.MerchantTerminalTypeEnum.Hosted);
                            if (!hostedPageTerminal) {
                                return transactionQuery(false);
                            }
                            this.registerContext.setSelectedTerminal(hostedPageTerminal);
                            this.$rootScope.$broadcast(Components.Common.Messages.DRAWER_TERMINAL_UPDATE, {}, hostedPageTerminal.id);
                            return transactionQuery(true);
                        })
                        .catch(() => {
                            return transactionQuery(false);
                        });
                    return;
                }

                return transactionQuery(false);
            }

            this.registerContext.removeTicketPaymentLine(paymentLine);

            this.addBalanceAsPayment();

            if (processedPayment.creditCardProcessorStatus) {
                let message = this.getProcessedPaymentMessage(processedPayment);
                this.busyService.stop();
                return this.modalService.alert(message, Components.Modal.ModalTranslationModes.DoNotTranslate).then(() => {
                    this.busyService.start();
                    return this.printDeclineReceipt(processedPayment);
                });
            }

            this.fullReset();

            // if we implement a block before the call, then we need to unblock the block here!
        };

        private printDeclineReceipt(payment)  {
            // print declined receipt for failed payments that are declined
            if (payment.paymentRequestStatus === Model.PaymentRequestStatusEnum.Failed &&
                payment.processorResponseCode === Model.ProcessorResponseCodeEnum.Decline) {
                return this.registerContext.printCreditCardReceipts([payment]);
            }
        }

        public initiateTimeoutWorkflow(paymentLine) {
            return this.modalService.confirmHandler("MESSAGE_CREDIT_CARD_TIMEOUT_INITIAL").then(response => {
                if (response) {
                    return this.captureManual(paymentLine, jQuery.extend(true, {}, paymentLine));
                }
                else {
                    this.registerContext.removeTicketPaymentLine(paymentLine);

                    this.addBalanceAsPayment();

                    return this.$q.resolve();
                }
            })
        }

        private captureManual(paymentRecInitial, paymentRecClone) {
            let modalOptions: Meevo.Components.Modal.IModalOptions = {
                templateUrl: "app/plugins/register/creditPayments/manualCreditCardPaymentModal.template.html",
                controller: "meevo.plugins.register.ManualCreditCardPaymentController",
                controllerAs: "vm",
                locals: {
                    paymentRequest: paymentRecClone,
                    paymentTypes: this.registerContext.paymentTypes
                },
                bindToController: true
            };
            return this.modalService.showCustom(modalOptions).then(response => {
                if (!response) {
                    return this.modalService.confirmHandler("MESSAGE_CREDIT_CARD_TIMEOUT_CANCEL").then(responseConfirm => {
                        if (responseConfirm) {
                            return this.initiateTimeoutWorkflow(paymentRecInitial);
                        }
                        else {
                            return this.captureManual(paymentRecInitial, paymentRecClone);
                        }
                    })
                }
                else {
                    const request = {
                        paymentRequest: paymentRecClone,
                        merchantTerminalId: this.registerContext.selectedTerminal.id,
                        externalTerminalId: this.registerContext.selectedTerminal.externalTerminalId,
                        merchantTerminalSerialNumber: this.registerContext.selectedTerminal.serialNumber,
                        merchantAccountId: this.registerContext.selectedDrawer.merchantAccountId,
                        sale: this.registerContext.sale,
                        isManual: true
                    };

                    return this.dataService
                        .customMethodData(`api/Drawer/${this.registerContext.selectedDrawer.id}/PaymentRequest`, "POST", request)
                        .then((response) => {
                            return this.processPaymentSuccessCallback(response);
                        })
                        .catch((response) => {
                            return this.processPaymentErrorCallback(response);
                        })
                        .finally(() => {
                            this.busyService.stop();
                        });
                }
            });
        }

        getCreditCardPaymentTypeUrl(paymentTypeSId: number) {
            const baseUrl = 'assets/images/icons/';

            switch (paymentTypeSId) {
                case 2:
                    return baseUrl + "cardtype_visa.svg";
                case 3:
                    return baseUrl + "cardtype_mastercard.svg";
                case 4:
                    return baseUrl + "cardtype_amex.svg";
                case 5:
                    return baseUrl + "cardtype_discover.svg";
                case 6:
                    return baseUrl + "cardtype_debit.svg";
                case 10:
                    return baseUrl + "cardtype_dinersclub.svg";
                default:
                    return baseUrl + "cardtype_blank.svg";
            }
        }

        private getSaleClients() {
            let clients =_(this.registerContext.sale.saleLines).chain().filter(saleLine =>
                    saleLine.saleType === Model.SalesTypeEnum.Service ||
                    saleLine.saleType === Model.SalesTypeEnum.ServiceCancellationFee ||
                    saleLine.saleType === Model.SalesTypeEnum.ServiceNoShowFee)
                    .map(saleLine => <ServiceSaleLineItem>saleLine.item)
                .filter(item => item.appointmentServiceSaleDetails &&
                    !_(item.appointmentServiceSaleDetails.payingClientId).isEmpty())
                .map(item => {
                    return {
                        id: item.appointmentServiceSaleDetails.payingClientId,
                        displayName: item.appointmentServiceSaleDetails.payingClientDisplayName, isPaying: true
                    }
                })
                .value();
            let clientIds = _(clients).pluck("id");
            clients.push(..._(this.registerContext.sale.saleLines).chain()
                .filter((saleLine) => saleLine.clientType === Model.ClientTypeEnum.Client &&
                    clientIds.indexOf(saleLine.client.id) === -1)
                .pluck("client").value());

            return clients;
        }

        private doesCreditCardExist(clientCard) {
            let allCreditCards = [...this.nonPayingClientCreditCardList, ...this.payingClientCreditCardList];
            return _(allCreditCards).any((card) =>
                card.clientGlobalId == clientCard.clientGlobalId &&
                card.cardType == clientCard.cardType &&
                card.creditCardLastFour == clientCard.creditCardLastFour &&
                moment(card.creditCardExpirationDate).format() == moment(clientCard.creditCardExpirationDate).format() &&
                this.isHsaFsaVisible(card) == this.isHsaFsaVisible(clientCard)
            );
        }

        isHsaFsaVisible(card) {
            return this.hasHsaFsaMerchantAccount && card && card.isHsaFsa &&
                   card.merchantProviderId == this.registerContext.selectedMerchantAccount.merchantProviderId;
        }

        isCardOnFile() {
            return this.sectionType === RegisterSectionTypeEnum.CardOnFile;
        }

        isHostedPage() {
            return !this.isCardOnFile() && this.registerContext.isHostedPage();
        }

        checkCreditCardOnFile(isHasCheck: boolean = false) {
            this.useCreditCardOnFile = false;

            if (this.isCardOnFile() || isHasCheck) {
                this.payingClientCreditCardList = [];
                this.nonPayingClientCreditCardList = [];
                const clients = this.getSaleClients();
                this.loadCreditCardList(clients).then((response: any) => {
                    clients.forEach((client) => {
                        const clientCards = _(response.data).where({ clientGlobalId: client.id });
                        clientCards.forEach((clientCard) => {
                            const creditCardExists = this.doesCreditCardExist(clientCard);

                            if (!creditCardExists) {
                                clientCard.clientDisplayName = client.displayName;
                                if (client.isPaying) {
                                    this.payingClientCreditCardList.push(clientCard);
                                }
                                else {
                                    this.nonPayingClientCreditCardList.push(clientCard);
                                }
                            }

                            if (this.creditCardSelected && this.creditCardSelected.id === clientCard.id) {
                                this.creditCardChanged(clientCard);
                            }
                        });
                    });
                });
            }
            else {
                if (!this.registerContext.isCreditMode) {
                    this.proceedWithPayment = true;
                }
            }
        }

        additionalClientChanged() {
            if (Meevo.Model.Guard.isSomething(this.additionalClient) &&
                Meevo.Model.Guard.isNothing(_(this.getSaleClients()).findWhere({ id: this.additionalClient.id }))) {
                this.loadCreditCardList([this.additionalClient]).then((response: any) => {
                    _(response.data).forEach((clientCard) => {

                        const creditCardExists = this.doesCreditCardExist(clientCard);

                        if (!creditCardExists) {
                            clientCard.clientDisplayName = this.additionalClient.displayName;
                            clientCard.isAdd = true;
                            this.nonPayingClientCreditCardList.push(clientCard);
                        }
                    });
                    this.additionalClient = null;

                    if (this.creditCardSelected && this.doesCreditCardExist(this.creditCardSelected)) {
                        this.creditCardChanged(this.creditCardSelected);
                    }
                });
            }
            else {
                this.additionalClient = null;
            }
        }

        removeAdditionalCard(index) {
            this.nonPayingClientCreditCardList.splice(index, 1);
        }

        private loadSurcharges() {
            const merchantAccountId = this.registerContext.selectedDrawer.merchantAccountId;

            if (!merchantAccountId ||
                (this.registerContext.selectedDrawer.merchantProviderId !== Model.MerchantAccountProviderEnum.Adyen &&
                 this.registerContext.selectedDrawer.merchantProviderId !== Model.MerchantAccountProviderEnum.AdyenCanada)) {
                return;
            }

            return this.registerContext.securityService
                .checkModuleAccess(SecurityKeys.REGISTER_MERCHANTACCOUNT_TAB_SURCHARGES + SecurityParts.KEY_PART_READ)
                .then(res => {
                    if (res !== Model.SecurityAccessEnum.Grant) {
                        this.surcharges = [];
                        return;
                    }

                    // exclude external tip amount from surcharge preview
                    const transactionAmount = this.registerContext.subtractNormalizedNumbers(+this.payment.amount, this.registerContext.getExternalTipAmountDue());

                    const url = `api/merchantAccount/${merchantAccountId}/surcharges?transactionAmount=${transactionAmount}`;
                    return this.registerContext.dataService
                        .customMethodData(url, 'GET', null)
                        .then(response => {
                            const surchargeData = response.data;
                            if (!surchargeData.isSurchargeEnabled) {
                                this.surcharges = [];
                                return;
                            }

                            this.surcharges = surchargeData.surcharges.filter(surcharge =>
                                surcharge.paymentCategory === Model.PaymentCategoryEnum.CreditDebit &&
                                surcharge.isEnabled);
                        });
                });

        }

        creditCardChanged(card) {
            if (this.registerContext.selectedMerchantAccount) {
                if (card.merchantProviderId != this.registerContext.selectedMerchantAccount.merchantProviderId) {
                    this.modalService.alert("MESSAGE_INVALID_MERCHANT");
                    return;
                }
            }

            this.payment.tempId = Guid.newGuid().toString();

            this.payment.surcharge = undefined;

            if (this.surcharges && this.surcharges.length > 0) {
                const surcharge = this.surcharges.find(surcharge =>
                    surcharge.paymentCategory === card.paymentCategory &&
                    surcharge.paymentSubCategory === card.paymentSubCategory);

                if (surcharge) {
                    this.payment.surcharge = surcharge.surcharge;
                }
            }

            this.creditCardSelected = card;
            this.requireSecurityCodeChanged();

            this.shouldProcessPayment = true;
            this.useCreditCardOnFile = true;
        }

        requireSecurityCodeChanged() {
            if (this.creditCardSelected && this.requireSecurityCode) {
                if (this.isSecurityCodeActive()) {
                    // card must have changed so reload
                    this.hostedPageService.reloadSecurityCode(this.securityCodeHostedPageId, this.creditCardSelected.cardType);
                    this.waitForSecurityCode();
                }
                else {
                    this.waitForSecurityCode(this.activateSecurityCodeHostedPage());
                }
            }
            else {
                this.hostedPageService.reset(this.securityCodeHostedPageId);
            }
        }

        private waitForSecurityCode(promise?) {
            if (!promise) {
                let deferred = this.$q.defer();
                // set a new promise so that there's always one waiting to return the
                // security code from the drop in compononent
                this.hostedPageService.setSecurityCodeDeferred(deferred);
                promise = deferred.promise;
            }

            promise.then((securityCodeResponse) => {
                console.info('securityCodeResponse', securityCodeResponse);
                this.creditCardSelected[this.encryptedSecurityCodeProp] = securityCodeResponse.paymentMethod.encryptedSecurityCode;
                this.processPayment();
                this.waitForSecurityCode();
            })
        }

        private loadCreditCardList(clients): any {
            if (clients.length > 0) {
                let request = {
                    criteria: {
                        isApproved: true,
                        clientIds: _(clients).pluck("id"),
                        IncludeClientPaymentAccounts: true,
                        IncludeMembershipPaymentAccounts: true,
                        additionalPaymentAccountIds: null
                    }
                };

                if (this.registerContext.paymentPlansEnabled) {
                    let requestPA = {
                        clientIds: _(clients).pluck("id")
                    };
                    return this.dataService.customMethodData(`api/PaymentPlan/PointOfSalePaymentAccount/List`, "POST", requestPA).then((response) => {
                        var clientPaymentAccountPairList;
                        if (response && response.data) {
                            clientPaymentAccountPairList = response.data;
                            request.criteria.additionalPaymentAccountIds = _(clientPaymentAccountPairList).pluck("paymentAccountId");
                        }
                        return this.dataService.customMethodData(`api/PaymentAccount/List`, "POST", request).then((paymentAccounts) => {
                            _(clientPaymentAccountPairList).each((pair) => {
                                let paymentAccount = _(paymentAccounts.data).findWhere({ id: pair.paymentAccountId });
                                if (paymentAccount) {
                                    paymentAccount.clientGlobalId = pair.clientId;
                                }
                            });
                            return paymentAccounts;
                        });
                    });
                }
                else {
                    return this.dataService.customMethodData(`api/PaymentAccount/List`, "POST", request);
                }
            }
            else {
                return this.$q.resolve([]);
            }
        }

        addPaymentIsDisabled() {
            if (this.isCardOnFile() && Model.Guard.isNothing(this.creditCardSelected)) {
                return false;
            }

            return +this.payment.amount === 0 || +this.payment.amount < this.externalTipAmount;
        }

        private getPaymentRequestBasedOnRefundItems() {
            this.registerContext.getPaymentRequestBasedOnRefundItems().then((response) => {
                this.setCardPaymentList(response);
                this.limitAvailablePaymentRequestsBasedOnProcessedRefunds();
            });
        }

        setCardPaymentList(paymentRequests: any[]) {
            this.cardPaymentList = paymentRequests.filter(paymentRequest => paymentRequest.amount > 0);
           
            for (const card of this.cardPaymentList) {
                this.setMinimumAmount(card);
            }

            this.updateMaxRefund();

            if (!this.payment) {
                this.payment = this.getNewPayment();
            }

            this.updateTotalRefundValue(null);
        }

        private setMinimumAmount(card): number {
            if (!this.registerContext || !this.registerContext.sale || !this.registerContext.sale.tipLines) {
                return 0;
            }

            card.minimumAmount = 0;

            for (const paymentRequest of card.paymentRequestList) {
                if (!paymentRequest.isAuthorization) {
                    continue;
                }

                const matchingTipLine = this.registerContext.sale.tipLines.some((tipLine) => 
                    tipLine.externalTipTransactionId &&
                    tipLine.isRefund &&
                    paymentRequest.locationId == tipLine.refundLocationId &&
                    paymentRequest.saleDate.valueOf() === tipLine.refundSaleDate.valueOf() &&
                    paymentRequest.saleId == tipLine.refundSaleId);

                const matchingSaleLine = this.registerContext.sale.saleLines.some((saleLine) => 
                    saleLine.externalTipTransactionId &&
                    saleLine.isRefund &&
                    paymentRequest.locationId == saleLine.refundLocationId &&
                    paymentRequest.saleDate.valueOf() === saleLine.refundSaleDate.valueOf() &&
                    paymentRequest.saleId == saleLine.refundSaleId);

                if (!matchingTipLine && !matchingSaleLine) {
                    continue;
                }

                card.isAuthorization = paymentRequest.isAuthorization;

                if (this.registerContext.hasProjectedRefund(this.registerContext.sale)) {
                    continue;
                }

                const externalTipAmount = Math.abs(this.registerContext.getExternalTipRefundDue(this.registerContext.sale));
                card.minimumAmount = externalTipAmount;
                card.amountToRefund = externalTipAmount;
                break;
            }

            return card.minimumAmount;
        }

        updateMaxRefund() {
            const minimumAmountCard = this.cardPaymentList.find(card => card.minimumAmount > 0);
            const minimumAmount = minimumAmountCard ? minimumAmountCard.minimumAmount : 0;

            const changeDue = this.registerContext.getChangeDue();
            const maxRefund = Math.max(this.registerContext.subtractNormalizedNumbers(changeDue, minimumAmount), 0);
            const projectedRefund = this.registerContext.sale.paymentLines.find(paymentLine => paymentLine.amount < 0 && paymentLine.paymentRequest && paymentLine.paymentRequest.isAuthorization);
            const projectedRefundAmount = projectedRefund ? Math.abs(projectedRefund.amount) : 0;

            for (const card of this.cardPaymentList) {
                const cardOriginalAmount = card.paymentRequestList.reduce((total, paymentRequest) => this.registerContext.addNormalizedNumbers(total, paymentRequest.amount), 0);
                if (card.isAuthorization) {
                    card.amount = Math.max(minimumAmount, Math.min(cardOriginalAmount - projectedRefundAmount, Math.max(changeDue, 0)));
                } else {
                    card.amount = Math.min(cardOriginalAmount, maxRefund);
                }
            }
        }

        updateTotalRefundValue(card: any) {
            const total = this.registerContext.normalizeDecimalPlaces(this.cardPaymentList.reduce((sum, card) => sum + card.amountToRefund, 0));

            if (card && total > this.registerContext.getCreditBackToClient()) {
                return this.modalService.alert("MESSAGE_REFUNDAMOUNT_GREATERTHAN_CHANGEDUE").then(() => {
                    card.amountToRefund = card.minimumAmount || 0;
                });
            }

            // null card means programmatic refresh, and
            // non-null card means user just edited that card's refund amount
            if (card || total > 0) {
                this.payment.requestedAmount = this.payment.amount = total;
            }

            this.shouldProcessPayment = true;
        }

        autoRefund() {
            let amountToDistribute = this.registerContext.getCreditBackToClient();

            for (const card of this.cardPaymentList) {
                if (amountToDistribute > 0) {
                    card.amountToRefund = Math.min(card.amount, amountToDistribute);
                    amountToDistribute = this.registerContext.subtractNormalizedNumbers(amountToDistribute, card.amountToRefund);
                }
                else {
                    card.amountToRefund = 0;
                }
            }

            const total = this.registerContext.normalizeDecimalPlaces(this.cardPaymentList.reduce((sum, card) => sum + card.amountToRefund, 0));
            this.payment.requestedAmount = this.payment.amount = total;

            this.shouldProcessPayment = true;
        }
       
        private processCredit() {
            if (this.isCardOnFile()) {
                if (this.creditCardSelected) {
                    this.processCreditWithOtherCard();
                }
                return null;
            }

            if (!this.validate()) {
                return null;
            }

            // check if user has entered any amount on any card
            if (!_.find(this.cardPaymentList, (cpl) => cpl.amountToRefund > 0 )) {
                this.modalService.alert("MESSAGE_ALLOCATE_CREDIT_REFUND_AMOUNT");
                return null;
            }

            this.unallocatedAmountToRefund = 0;

            this.shouldProcessPayment = true;

            this.cardCreditRequestList = [];

            for (const card of this.cardPaymentList) {
                let availCardAmountToRefund: number = Number(card.amountToRefund);
                if (availCardAmountToRefund > 0) {
                    // break up amountToRefund between card.paymentRequests/old transactions
                    for (const origPaymentRequest of card.paymentRequestList) {
                        if (availCardAmountToRefund <= 0) {
                            break;
                        }
                        
                        if (origPaymentRequest.amount > 0) {

                            let creditPaymentRequest = this.getNewPayment();
                            creditPaymentRequest.amount = -Math.min(availCardAmountToRefund, origPaymentRequest.amount);
                            creditPaymentRequest.requestedAmount = -Math.min(availCardAmountToRefund, origPaymentRequest.amount);
                            creditPaymentRequest.parentId = origPaymentRequest.id;
        
                            let request = {
                                paymentRequest: creditPaymentRequest,
                                merchantTerminalId: null,
                                externalTerminalId: null,
                                merchantTerminalSerialNumber: null,
                                merchantAccountId: this.registerContext.selectedDrawer.merchantAccountId,
                                sale: this.registerContext.sale,
                                paymentAccountId: null,
                                hasMeevoPaySplitFunding: origPaymentRequest.isAuthorization,
                            };

                            if (!this.isCardOnFile()) {
                                if (this.registerContext.selectedTerminal) {
                                    request.merchantTerminalId = this.registerContext.selectedTerminal.id;
                                    request.externalTerminalId = this.registerContext.selectedTerminal.externalTerminalId;
                                    request.merchantTerminalSerialNumber = this.registerContext.selectedTerminal.serialNumber;
                                }
                            }

                            this.cardCreditRequestList.push(request);

                            availCardAmountToRefund = this.registerContext.subtractNormalizedNumbers(availCardAmountToRefund, Math.abs(creditPaymentRequest.amount));
                        }
                    }
                }
            }

            this.processCreditNow();
        }

        private processCreditUnlinked() {
            if (!this.validate(true)) {
                return null;
            }

            this.shouldProcessPayment = true;

            this.cardCreditRequestList = [];

            let amountToDistribute: number = Number(this.registerContext.getChangeDue());

            this.payment = this.getNewPayment();

            this.payment.amount = -amountToDistribute;
            this.payment.requestedAmount = -amountToDistribute;

            this.processPayment();
        }

        private processCreditNow() {
            if (!this.cardCreditRequestList || this.cardCreditRequestList.length === 0) {
                return null;
            }

            this.busyService.start("LABEL_CREDIT_DEBIT_PENDING_PROCESSING", true);

            this.cardCreditRequestList.forEach((item) => {
                this.registerContext.addPayment(item.paymentRequest, false);
            });

            const request = {
                saleKeys: this.registerContext.getRefundSaleKeys(),
                saleTipKeys: this.registerContext.getRefundTipKeys(),
                originalCardPayments: this.cardPaymentList,
                paymentRequestPostList: this.cardCreditRequestList
            };

            return this.dataService
                .customMethodData(`api/Drawer/${this.registerContext.selectedDrawer.id}/RefundPaymentRequest/List`, "POST", request)
                .then((response) => {
                    if (response.status == 409) { // conflict
                        this.cleanupRefund();
                        this.busyService.stop();
                        return this.modalService.alert(response.data.responseStatus.message).then(() => {
                            this.busyService.start();
                            return null;
                        });
                    }
                    else if (response && response.data && response.data.length > 0) {
                        let paymentRequestsToBePrinted: Model.PaymentRequest[] = [];
                        let failedPaymentRequests: Model.PaymentRequest[] = [];
                        let partiallySucceeded: boolean = false;

                        response.data.forEach((processedPayment: Model.PaymentRequest) => {
                            if (!processedPayment) {
                                return;
                            }

                            // get the pending payment line in the Sale object - there should be only one!
                            let paymentLine: Model.SaleCreditCardPayment = _(this.registerContext.sale.paymentLines).find(
                                (paymentLine) => paymentLine.tempId.toLowerCase() == processedPayment.tempId.toString().toLowerCase()
                            );

                            if (!paymentLine) {
                                return;
                            }

                            if (processedPayment.paymentRequestStatus === Model.PaymentRequestStatusEnum.Approved) {
                                paymentRequestsToBePrinted.push(processedPayment);

                                this.registerContext.updatePaymentLineWithPaymentRequest(processedPayment, paymentLine);
                            }
                            else {
                                if (processedPayment.paymentRequestStatus === Model.PaymentRequestStatusEnum.Failed
                                    && processedPayment.creditCardProcessorStatus.code === Model.ProcessingStatusEnum.Declined) {
                                    paymentRequestsToBePrinted.push(processedPayment);
                                }

                                failedPaymentRequests.push(processedPayment);

                                this.registerContext.removeTicketPaymentLine(paymentLine);

                                // add the amount to be refunded as Cash
                                this.unallocatedAmountToRefund = this.registerContext.addNormalizedNumbers(this.unallocatedAmountToRefund, Math.abs(paymentLine.requestedAmount));
                            }
                        });

                        if (failedPaymentRequests.length > 0) {
                            let errorMessages: string[] = [];
                            failedPaymentRequests.forEach((processedPayment: Model.PaymentRequest) => {
                                if (processedPayment.creditCardProcessorStatus) {
                                    let message = this.$translate.instant(processedPayment.creditCardProcessorStatus.message);
                                    if (message === processedPayment.creditCardProcessorStatus.message) {
                                        message = this.$translate.instant("MESSAGE_CC_STATUS_UNKNOWN") + ` (${processedPayment.creditCardProcessorStatus.message})`;
                                    }
                                    message += this.registerContext.appendPaymentAlertMessage(processedPayment.creditCardProcessorStatus);
                                    errorMessages.push(message);
                                }
                                else {
                                    errorMessages.push(this.$translate.instant("MESSAGE_CC_STATUS_UNKNOWN") + ` (${processedPayment.creditCardType} ${processedPayment.creditCardNumber} ${processedPayment.creditCardExpirationDate})`);
                                }
                            });
                            this.busyService.stop();
                            return this.modalService.alert(errorMessages.join("<br/><br/>"), Components.Modal.ModalTranslationModes.AsHtml).then(() => {
                                this.busyService.start("LABEL_CREDIT_DEBIT_PENDING_PROCESSING", true);
                                return paymentRequestsToBePrinted;
                            });
                        }

                        return paymentRequestsToBePrinted;
                    }
                })
                .then((paymentRequestsToBePrinted: Model.PaymentRequest[]) => {
                    const finishExecution = () => {
                        this.fullReset();
                        let section = this.registerContext.getChangeDue() > 0 || this.registerContext.hasExternalTipRefundDue()
                            ? RegisterSectionTypeEnum.ChangeOptions
                            : RegisterSectionTypeEnum.PaymentOptions;

                        // give MeevoTips kiosk a chance to process any potential tips
                        let forcePaymentOptions = false;
                        if (!this.registerContext.hasPaymentAuthorization(this.registerContext.sale)) {
                            const hasNonRefundSaleLines = this.registerContext.sale.saleLines.filter(saleLine => 
                                !saleLine.isRefund && 
                                saleLine.employeeId !== this.registerContext.HouseEmployee.id);
                            if (hasNonRefundSaleLines.length > 0) {
                                section = RegisterSectionTypeEnum.PaymentOptions;
                                forcePaymentOptions = true;
                            }
                        }

                        return this.registerContext.selectSection(section).finally(() => {
                            this.registerContext.finishSection(forcePaymentOptions);
                        });
                    };

                    this.registerContext.recalculateChangeDue();
                    this.busyService.stop();

                    if (!paymentRequestsToBePrinted || paymentRequestsToBePrinted.length === 0) {
                        return finishExecution();
                    }

                    const continueExecution = () => {
                        this.registerContext.printCreditCardReceipts(paymentRequestsToBePrinted).finally(() => {
                            if (this.unallocatedAmountToRefund == 0) {
                                return finishExecution();
                            }
                            // check if there are payment request(s) that succeeded, and if there are, ensure to exclude the amount that was already refunded.
                            this.limitAvailablePaymentRequestsBasedOnProcessedRefunds();
                        });
                    };

                    // there may be multiple payment alerts, but only showing one message if any exist on success path
                    const inconclusivePaymentRequest = _(paymentRequestsToBePrinted).find(paymentRequest =>
                        this.registerContext.hasPaymentAlertMessage(paymentRequest.creditCardProcessorStatus));

                    if (inconclusivePaymentRequest) {
                        return this.modalService.alert(inconclusivePaymentRequest.creditCardProcessorStatus.paymentAlertMessage).then(() => {
                            return continueExecution();
                        });
                    }

                    return continueExecution();
                })
                .catch(() => {
                    this.cleanupRefund();
                })
                .finally(() => {
                    this.busyService.stop();
                });
        }

        private limitAvailablePaymentRequestsBasedOnProcessedRefunds() {
            if (!this.registerContext.sale.paymentLines) {
                return;
            }

            this.registerContext.sale.paymentLines.forEach((paymentLine) => {
                if (paymentLine.paymentCategory === Model.PaymentCategoryEnum.CreditDebit && paymentLine.amount < 0) {
                    let creditPaymentLine = (paymentLine as Meevo.Model.SaleCreditCardPayment);
                    if (creditPaymentLine.parentId != null && !Guid.empty.equals(new Guid(creditPaymentLine.parentId))) {
                        this.cardPaymentList.forEach((cardPaymentLine) => {
                            let matchedPaymentRequest = _.find(cardPaymentLine.paymentRequestList, (paymentRequest: Model.PaymentRequest) =>
                                paymentRequest.id.toString().toLowerCase() == creditPaymentLine.parentId.toString().toLowerCase());
                            if (matchedPaymentRequest) {
                                matchedPaymentRequest.amount = this.registerContext.subtractNormalizedNumbers(matchedPaymentRequest.amount, Math.abs(creditPaymentLine.amount));
                                cardPaymentLine.amount = this.registerContext.subtractNormalizedNumbers(cardPaymentLine.amount, Math.abs(creditPaymentLine.amount));
                                cardPaymentLine.amountToRefund = 0;
                            }
                        });
                    }
                }
            });
        }

        private cleanupRefund() {
            this.cardCreditRequestList.forEach((item) => {
                let paymentLine: Model.SaleCreditCardPayment = _.findWhere(this.registerContext.sale.paymentLines, { tempId: item.paymentRequest.tempId });
                if (paymentLine) {
                    this.registerContext.removeTicketPaymentLine(paymentLine);
                }
            });

            this.fullReset();
        }

        paymentLineRemoved(paymentLine) {
            if (this.registerContext.isCreditMode && paymentLine && paymentLine.amount < 0) {
                this.getPaymentRequestBasedOnRefundItems();
            }
        }

        processCreditWithOtherCard() {
            if (this.isHostedPage() && !this.registerContext.getMerchantAccountHasHostedPageUnlinkedRefundSupport()) {
                this.modalService.alert("LABEL_HOSTED_PAGE_UNLINKED_REFUND_NOT_SUPPORTED");
                return;
            }

            if (this.registerContext.selectedMerchantAccount.restrictUnlinkedCreditCardRefunds) {
                this.modalService.alert("MESSAGE_UNLINKED_CREDITCARD_REFUND_NOT_ALLOWED");
                return;
            }

            this.addBalanceAsPayment();
            this.shouldProcessPayment = true;
            this.processCreditUnlinked();
        }

        isPaymentTypeAllowedForRefund(paymentType: Model.PaymentTypeListItem): boolean {
            return !this.registerContext.isCreditMode || paymentType.isRefundable;
        }

        isPaymentOptionsSectionVisible(): boolean {
            const isVisible = !this.creditCardProcessingEnabled ||
                this.displayPaymentAmount ||
                this.proceedWithPayment ||
                (this.registerContext.isCreditMode && this.creditCardProcessingEnabled && !this.registerContext.getMerchantAccountHasLinkedRefundSupport());

            return isVisible;
        }
    }
}